import { extractBlankValues } from './extractBlankValues';

interface CueTemplate {
  conceptResults: any[];
  cue: string;
  feedback: string;
  optimal: boolean;
}

interface BlankByBlankGrading {
  [key: string]: CueTemplate[]; // blank1, blank2, etc.
}

interface Response {
  text: string;
  [key: string]: any;
}

interface Question {
  blankByBlankGrading?: BlankByBlankGrading;
  caseInsensitive?: boolean;
  prompt: string;
  [key: string]: any;
}

/**
 * Filter responses to show only those with a suboptimal cue for a specific blank.
 *
 * @param responses - Array of student responses
 * @param question - Question object with prompt and blankByBlankGrading data
 * @param blankIndex - Zero-based index of the blank to check (0 = blank1, 1 = blank2, etc.)
 * @returns Filtered array of responses where the specified blank is suboptimal
 *
 * @example
 * filterBySuboptimalBlank(responses, question, 0)
 * // Returns only responses where blank1 is suboptimal
 */
export function filterBySuboptimalBlank(
  responses: Response[],
  question: Question,
  blankIndex: number
): Response[] {
  if (!responses || !question || !question.blankByBlankGrading) {
    return [];
  }

  const caseInsensitive = question.caseInsensitive || false;
  const blankKey = `blank${blankIndex + 1}`;
  const cueTemplates = question.blankByBlankGrading[blankKey];

  if (!cueTemplates) {
    return responses;
  }

  return responses.filter(response => {
    const blankValues = extractBlankValues(response.text, question.prompt);
    const studentAnswer = blankValues[blankIndex] || '';

    // If the response is optimal, or human-graded (will have either a true or false optimal value), return false
    if ([true, false].includes(response.optimal)) {
      return false
    }

    // Normalize for comparison
    const normalizedAnswer = caseInsensitive
      ? studentAnswer.toLowerCase()
      : studentAnswer;

    // Find matching cue template
    const matchedCue = cueTemplates.find(template => {
      const normalizedCue = caseInsensitive
        ? template.cue.toLowerCase()
        : template.cue;

      return normalizedCue === normalizedAnswer;
    });

    // Suboptimal if: matched a non-optimal cue OR no match at all
    return matchedCue ? !matchedCue.optimal : true;
  });
}

/**
 * Filter responses by specific cue values for each blank.
 *
 * @param responses - Array of student responses
 * @param question - Question object with prompt
 * @param searchValues - Array of search values (one per blank, empty string = any cue)
 * @returns Filtered array of responses matching the search criteria
 *
 * @example
 * filterByBlankCues(responses, question, ['a', 'good'])
 * // Returns only responses where blank1='a' AND blank2='good'
 *
 * filterByBlankCues(responses, question, ['a', ''])
 * // Returns only responses where blank1='a' (blank2 can be anything)
 */
export function filterByBlankCues(
  responses: Response[],
  question: Question,
  searchValues: string[]
): Response[] {
  if (!responses || !question || !searchValues || searchValues.length === 0) {
    return responses || [];
  }

  const caseInsensitive = question.caseInsensitive || false;

  return responses.filter(response => {
    const blankValues = extractBlankValues(response.text, question.prompt);

    // Check each blank's search filter
    return searchValues.every((searchValue, index) => {
      // undefined/null search value = accept any cue for this blank
      if (searchValue === undefined || searchValue === null) {
        return true;
      }

      // Trim search value
      const trimmedSearch = searchValue.trim();

      // Empty search value after trim = accept any cue for this blank
      if (!trimmedSearch) {
        return true;
      }

      const studentAnswer = blankValues[index] || '';

      // Special marker [empty blank] to search for empty blanks specifically
      if (trimmedSearch.toLowerCase() === '[empty blank]') {
        return studentAnswer === '';
      }

      // Normalize for comparison
      const normalizedSearch = caseInsensitive
        ? trimmedSearch.toLowerCase()
        : trimmedSearch;

      const normalizedAnswer = caseInsensitive
        ? studentAnswer.toLowerCase()
        : studentAnswer;

      return normalizedAnswer === normalizedSearch;
    });
  });
}
